/**
 * Where a call of the web app goes, with the gateway (VITE_API_MODE=gateway, the default) or without it
 * (VITE_API_MODE=direct, docs/NO_GATEWAY.md). No imports on purpose: the backend's scripts/no-gateway-smoke.ts walks the
 * same mapping.
 *
 *   web path              gateway                         direct (the backend, same origin)
 *   /backend/<rest>       /api/v1/backend/<rest>          /admin/v1/<rest>
 *   /dispatch/<rest>      /api/v1/dispatch/<rest>         /api/<rest>            (compat; its envelope is unwrapped here)
 *   anything else         /api/v1/<path>                  /api/v1/<path>         (gateway-only leftovers, see the doc)
 */

export type ApiMode = 'gateway' | 'direct'
export type PathKind = 'backend' | 'dispatch' | 'other'

export const apiModeOf = (value: unknown): ApiMode => (String(value ?? '').trim().toLowerCase() === 'direct' ? 'direct' : 'gateway')

export function pathKind(path: string): PathKind {
  if (path.startsWith('/backend/')) return 'backend'
  if (path.startsWith('/dispatch/')) return 'dispatch'
  return 'other'
}

/**
 * The gateway's own routes the backend now serves under /admin/v1 (direct mode only): the WhatsApp group reader and the
 * cameras, reached through the backend's sidecar proxy (src/modules/wa-reader-proxy, docs/SIDECARS.md).
 */
export const DIRECT_ALIASES: readonly [string, string][] = [
  ['/ext/wa-reader', '/admin/v1/wa-reader'],
  ['/ext/cam-link/', '/admin/v1/cam-link/'],
  ['/ext/docs/', '/admin/v1/docs/'],
  // the last gateway-only staff routes, on the backend (src/modules/web-session/leftovers.ts)
  ['/ext/runners-map/', '/admin/v1/runners-map/'],
  ['/ext/signup-attempts', '/admin/v1/signup-attempts'],
  ['/ext/portal-notices', '/admin/v1/portal-notices'],
  ['/integrations/couriers', '/admin/v1/integrations/couriers'],
  ['/integrations/history/', '/admin/v1/integrations/history/'],
  ['/store/orders', '/admin/v1/materials/store/orders'],
  ['/admin/diagnostics/performance', '/admin/v1/admin/diagnostics/performance'],
]

/** The URL path (and query, untouched) a web path is sent to. */
export function resolveApiPath(path: string, mode: ApiMode, apiBase = '/api/v1'): string {
  if (mode === 'direct') {
    if (path.startsWith('/backend/')) return `/admin/v1/${path.slice('/backend/'.length)}`
    if (path.startsWith('/dispatch/')) return `/api/${path.slice('/dispatch/'.length)}`
    for (const [from, to] of DIRECT_ALIASES) {
      // a prefix ending in «/» covers what is under it; otherwise the path itself, its sub-paths and its query
      const hit = path.startsWith(from) && (from.endsWith('/') || path.length === from.length || '/?'.includes(path.charAt(from.length)))
      if (hit) return `${to}${path.slice(from.length)}`
    }
  }
  return `${apiBase}${path}`
}

/** A compat answer that is not a session's end: the security screens answer 401 for a wrong code. */
const SECURITY = /^\/dispatch\/Security\//i
/** The sign-in calls answer 401 for a wrong password or code: never a lost session. */
export const isSignInPath = (path: string) => path.startsWith('/auth/login') || /^\/dispatch\/Users\/V1\/Login/i.test(path)

export interface CompatOk {
  ok: true
  id: string | number | null
  message: string | null
  data: unknown
  data2: unknown
  summary: unknown
  blocked?: unknown
  overridden?: unknown
}
export interface CompatFail {
  ok: false
  status: number
  error: { code: string; message: string; details?: unknown }
}

/** Dispatch's own validation text inside a refused answer, when the envelope only says «error» (the gateway's upstreamDetail). */
export function upstreamDetail(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const d = data as { generalResponse?: { message?: string }; badResponses?: { awb?: string; errors?: { message?: string }[] }[]; notValid?: { awb?: string; errMessage?: string }[] }
  const bad = (d.badResponses ?? []).flatMap((b) => (b.errors ?? []).map((e) => e.message).filter(Boolean).map((m) => (b.awb ? `${b.awb}: ${m}` : m!)))
  const notValid = (d.notValid ?? []).map((n) => (n?.errMessage ? `${n.awb ?? ''}: ${n.errMessage}` : '')).filter(Boolean)
  return [d.generalResponse?.message, ...bad, ...notValid].filter(Boolean).join(' · ').slice(0, 500)
}

/**
 * The compat answer `{ success, id, message, data, data2, summary }` in the shape the screens read from the gateway's
 * proxy: `{ ok: true, ... }`, or the error the proxy raised (422 UPSTREAM_REJECTED for `success: false`, the backend's
 * own `{ error }` for a guard such as ASSIGN_BLOCKED, 401 for no session).
 */
export function fromCompat(status: number, body: unknown, path = ''): CompatOk | CompatFail {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
  const own = b.error && typeof b.error === 'object' ? (b.error as { code?: string; message?: string; details?: unknown }) : null
  const message = typeof b.message === 'string' && b.message && b.message !== 'error' ? b.message : ''
  if (status === 401 && SECURITY.test(path)) return { ok: false, status: 422, error: { code: 'INVALID_CODE', message: message || 'رمز التحقق غير صحيح' } }
  if (own?.code) return { ok: false, status: status >= 400 ? status : 422, error: { code: own.code, message: own.message || message || 'تم رفض الطلب', ...(own.details !== undefined ? { details: own.details } : {}) } }
  if (status === 401) return { ok: false, status, error: { code: 'UNAUTHENTICATED', message: message || 'انتهت الجلسة، يرجى تسجيل الدخول مرة اخرى' } }
  if (status === 403) return { ok: false, status, error: { code: 'FORBIDDEN', message: message || 'ليس لديك صلاحية لهذه العملية' } }
  if (status === 501) return { ok: false, status, error: { code: 'NOT_IMPLEMENTED', message: message || 'هذه العملية غير متاحة بعد' } }
  if (b.success === false || status >= 400) {
    return {
      ok: false,
      status: status >= 500 ? 502 : 422,
      error: { code: 'UPSTREAM_REJECTED', message: message || upstreamDetail(b.data) || 'تم رفض الطلب', details: { id: b.id ?? null, data: b.data ?? null, data2: b.data2 ?? null } },
    }
  }
  const ok: CompatOk = { ok: true, id: (b.id as string | number | null | undefined) ?? null, message: (b.message as string | null | undefined) ?? null, data: b.data ?? null, data2: b.data2 ?? null, summary: b.summary ?? null }
  if ('blocked' in b) ok.blocked = b.blocked
  if ('overridden' in b) ok.overridden = b.overridden
  return ok
}
